/**
 * Métodos de envío (listado paginado). Mismo envelope que el resto del API
 * (`records.code === 'SUCCESS'`, `records.data`, `totalCount` en raíz).
 *
 * Consume `GET shipping-methods?page&size`. El servicio se queda **solo** con
 * `shipCode` y `shipName` por elemento; el resto de campos del API
 * (`country`, `shippingCost`, `trackingServiceURL`, `chargingMethod`,
 * `typeTransport`, fechas, etc.) se descarta aquí para no acoplarlos a la UI.
 *
 * @typedef {{ shipCode: string, shipName: string }} ShippingMethodOption
 */
import axiosInstance from '../utils/axiosInstance';

/** Tamaño de página para traer todos los métodos en pocas peticiones (`shipping-methods?page=1&size=100`). */
export const SHIPPING_METHODS_FETCH_PAGE_SIZE = 100;

/**
 * Obtiene todos los métodos de envío paginando hasta cubrir `totalCount`
 * (o hasta una página parcial). Bearer vía `axiosInstance`.
 *
 * @param {{ signal?: AbortSignal, pageSize?: number }} [options]
 * @returns {Promise<ShippingMethodOption[]>}
 */
export async function fetchAllShippingMethods(options = {}) {
  const { signal, pageSize = SHIPPING_METHODS_FETCH_PAGE_SIZE } = options;
  /** @type {ShippingMethodOption[]} */
  const all = [];
  let page = 1;

  for (;;) {
    const { data } = await axiosInstance.get(`shipping-methods?page=${page}&size=${pageSize}`, {
      signal
    });

    const records = data?.records;
    if (!records || records.code !== 'SUCCESS') {
      const msg = records?.message;
      throw new Error(typeof msg === 'string' && msg.trim() ? msg : 'Error al obtener métodos de envío');
    }

    const batch = Array.isArray(records.data) ? records.data : [];
    for (const item of batch) {
      const code = item?.shipCode != null ? String(item.shipCode).trim() : '';
      const name = item?.shipName != null ? String(item.shipName).trim() : '';
      if (!code || !name) continue;
      all.push({ shipCode: code, shipName: name });
    }

    const total =
      typeof data?.totalCount === 'number' && Number.isFinite(data.totalCount) && data.totalCount >= 0
        ? data.totalCount
        : null;

    if (total != null && all.length >= total) break;
    if (batch.length === 0) break;
    if (batch.length < pageSize) break;

    page += 1;
    if (page > 500) break;
  }

  all.sort((a, b) => a.shipName.localeCompare(b.shipName, undefined, { sensitivity: 'base' }));
  return all;
}
